import { ExpertProfileCreateDto, ExpertProfileSelectDto, ExpertProfileType } from '@@types/api.types';
import { Modal } from '@components/core/modal.component.tsx';
import { ModalSteps, StepState, useSteps } from '@components/shared/modal-steps.component.tsx';
import { errorToast } from '@hooks/toast.hook.tsx';
import { useCreateExpertProfile, useUpdateExpertProfile } from '@queries/expert-profile.query.ts';
import { useGetCurrentUser } from '@queries/users.query.ts';
import {
  backgroundStepSchema,
  expertProfileCreateSchema,
  expertiseStepSchema,
  profileStepSchema,
  typeStepSchema,
} from '@schemas/expert-profile.schema.ts';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { ZodTypeAny, z } from 'zod';
import { ExpertProfileModalBackgroundStep } from './expert-profile-modal-background-step.component.tsx';
import { ExpertProfileModalExpertiseStep } from './expert-profile-modal-expertise-step.component.tsx';
import { ExpertProfileModalProfileStep } from './expert-profile-modal-profile-step.component.tsx';
import { ExpertProfileTypeStep } from './expert-profile-modal-type-step.component.tsx';

export type FormFieldName = keyof ExpertProfileCreateDto;
interface ExpertProfileModalProps {
  expertProfile?: ExpertProfileSelectDto | null;
  isEdit?: boolean;
  isOpen: boolean;
  onClose: (submit?: boolean) => void;
}

type FormErrors = z.inferFormattedError<typeof expertProfileCreateSchema, string>;

export interface ExpertProfileComponentProps {
  errors: FormErrors;
  formData: ExpertProfileCreateDto;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  isEdit?: boolean;
  setFormData: React.Dispatch<React.SetStateAction<ExpertProfileCreateDto>>;
}

enum Steps {
  TYPE = 0,
  PROFILE = 1,
  BACKGROUND = 2,
  EXPERTISE = 3,
}

interface StepConfig {
  component: FunctionComponent<ExpertProfileComponentProps>;
  guidance: string;
  label: string;
  schema: ZodTypeAny;
  step: Steps;
}

const stepData: StepConfig[] = [
  {
    component: ExpertProfileTypeStep,
    guidance: 'What kind of expert are you?',
    label: 'Type',
    schema: typeStepSchema,
    step: Steps.TYPE,
  },
  {
    component: ExpertProfileModalProfileStep,
    guidance: 'Enter your details of personal info',
    label: 'Profile',
    schema: profileStepSchema,
    step: Steps.PROFILE,
  },
  {
    component: ExpertProfileModalBackgroundStep,
    guidance: 'Enter your personal background details',
    label: 'Background',
    schema: backgroundStepSchema,
    step: Steps.BACKGROUND,
  },
  {
    component: ExpertProfileModalExpertiseStep,
    guidance: 'Enter your expertise details',
    label: 'Expertise',
    schema: expertiseStepSchema,
    step: Steps.EXPERTISE,
  },
];

const defaultFormData: ExpertProfileCreateDto = {
  type: ExpertProfileType.VETERAN,
  linkedinUrl: undefined,
  websiteUrl: undefined,
  countryOfResidence: undefined,
  stateOfResidence: undefined,
  cityOfResidence: undefined,
  countryOfService: undefined,
  serviceId: undefined,
  yearsOfExperience: undefined,
  educations: [],
  communities: [],
  userId: '',
  capabilities: [],
  categories: [],
  domains: [],
  effects: [],
  mrlAssessmentRange: [],
  phases: [],
  trlAssessmentRange: [],
};

const emptyErrors: FormErrors = { _errors: [] };

export const ExpertProfileModal: FunctionComponent<ExpertProfileModalProps> = (props) => {
  const { isOpen, onClose, isEdit = false, expertProfile } = props;

  const [formData, setFormData] = useState<ExpertProfileCreateDto>(defaultFormData);
  const [errors, setErrors] = useState<FormErrors>(emptyErrors);
  const { data: currentUser } = useGetCurrentUser();
  const {
    step,
    stepsInfo,
    updateStepState,
    handleStepClick: handleStepClickBase,
    resetSteps,
    setStepsInfo,
  } = useSteps(stepData, Steps.TYPE, isEdit);

  const initialValues = useMemo(() => {
    return {
      ...defaultFormData,
      ...expertProfile,
      capabilities: expertProfile?.capabilities?.map((capability) => capability.id) ?? [],
      categories: expertProfile?.categories?.map((category) => category.id) ?? [],
      communities: expertProfile?.communities?.map((community) => community.id) ?? [],
      domains: expertProfile?.domains?.map((domain) => domain.id) ?? [],
      educations: expertProfile?.educations ?? [],
      effects: expertProfile?.effects?.map((effect) => effect.id) ?? [],
      mrlAssessmentRange: expertProfile?.mrlAssessmentRange ?? [],
      phases: expertProfile?.phases ?? [],
      serviceId: expertProfile?.service?.id,
      trlAssessmentRange: expertProfile?.trlAssessmentRange ?? [],
      type: expertProfile?.type ?? ExpertProfileType.VETERAN,
      userId: currentUser?.profile?.id || '',
    };
  }, [expertProfile, currentUser]);

  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (isEdit) {
      setStepsInfo((prevSteps) =>
        prevSteps.map((stepInfo) => ({
          ...stepInfo,
          state: stepInfo.step === step ? StepState.ACTIVE : StepState.NAVIGABLE,
        })),
      );
    }
  }, [isEdit, setStepsInfo, step]);

  const { mutateAsync: createExpertProfile, isPending: isCreatingProfile } = useCreateExpertProfile();
  const { mutateAsync: updateExpertProfile, isPending: isUpdatingProfile } = useUpdateExpertProfile();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isWithinCommandOrPopover = (e.target as HTMLElement)?.closest('[role="combobox"]');
    if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement || isWithinCommandOrPopover)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const validateCurrentStep = async () => {
    const currentStepConfig = stepData.find((s) => s.step === step);
    if (!currentStepConfig) return false;

    const result = currentStepConfig.schema.safeParse(formData);
    if (result.success) {
      setErrors(emptyErrors);
      return true;
    }
    errorToast({ description: 'Please fill in all required fields correctly.' });
    setErrors(result.error.format());
    return false;
  };

  const onCloseOverride = () => {
    resetSteps();
    setErrors(emptyErrors);
    setFormData(initialValues);
    onClose();
  };

  const handleSubmit = async () => {
    const result = expertProfileCreateSchema.safeParse(formData);
    if (!result.success) {
      setErrors(result.error.format());
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    if (expertProfile && expertProfile.id) {
      await updateExpertProfile(result.data);
    } else {
      await createExpertProfile(result.data);
    }

    onCloseOverride();
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;

    if (isEdit || step === Steps.EXPERTISE) {
      await handleSubmit();
    } else {
      updateStepState(step);
    }
  };

  const handleBack = async () => {
    if (isEdit) {
      const isValid = await validateCurrentStep();
      if (!isValid) return;
    }

    if (step > Steps.TYPE) {
      handleStepClickBase(step - 1);
    }
  };

  const handleStepClick = async (targetStep: Steps) => {
    if (targetStep === step) return;

    const isValid = await validateCurrentStep();
    if (!isValid) return;

    if (targetStep > step) {
      setStepsInfo((prevSteps) =>
        prevSteps.map((info) => (info.step === step ? { ...info, state: StepState.NAVIGABLE, isValid: true } : info)),
      );
    }
    handleStepClickBase(targetStep);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as FormFieldName;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const currentStepData = stepData.find((data) => data.step === step);

  return (
    <Modal
      footer={{
        rightButton: {
          loading: isCreatingProfile || isUpdatingProfile,
          onClick: handleNext,
          children: isEdit ? 'Update' : step === Steps.EXPERTISE ? 'Create Profile' : 'Continue',
        },
        leftButton:
          step !== Steps.TYPE && !isEdit
            ? {
                onClick: handleBack,
                children: 'Back',
                variant: 'secondary',
              }
            : undefined,
      }}
      header={{
        title: isEdit ? 'Update Expert Profile' : 'Create Your Expert Profile',
      }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width="640px"
    >
      <div className="flex flex-col gap-4 py-2 px-1">
        <ModalSteps
          stepsInfo={stepsInfo}
          onStepClick={handleStepClick}
        />
      </div>

      {currentStepData?.guidance && (
        <div className="text-md font-normal mx-3 text-[#1F1C1AB2] flex whitespace-pre-wrap my-5 mb-[30px]">
          {currentStepData?.guidance}
        </div>
      )}

      <div className="px-3 pb-6">
        {currentStepData?.component && (
          <currentStepData.component
            formData={formData}
            handleChange={handleChange}
            errors={errors}
            setFormData={setFormData}
            isEdit={isEdit}
          />
        )}
      </div>
    </Modal>
  );
};
